import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { sidebarMenuButtonVariants } from '@/shared/config/sidebar';
import { useSidebar } from '@/shared/hooks/useSidebar';
import { cn } from '@/shared/lib/cn';
import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import type * as React from 'react';

export function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = 'default',
  size = 'default',
  mobile = false,
  tooltip,
  className,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  const Comp = asChild ? Slot : 'button';
  // `open` is the live desktop-rail collapse state (global); the mobile-vs-desktop
  // density comes from the `mobile` prop the caller passes for its context.
  const { open } = useSidebar();

  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size, mobile }), className)}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  const tooltipProps = typeof tooltip === 'string' ? { children: tooltip } : tooltip;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" align="center" hidden={open || !!mobile} {...tooltipProps} />
    </Tooltip>
  );
}
